<script setup lang="ts">
import { computed, ref } from "vue";
import type { IconName } from "@/design/icons";
import Icon from "@/design/primitives/Icon.vue";
import { executionFacts } from "@/shared/agent-display/model-execution";
import { useAgentDisplayMode } from "@/shared/agent-display/use-agent-display-mode";
import RichText from "@/shared/rich-text/RichText.vue";
import { isPending } from "../api/tool-labels";
import type { ToolTask } from "../composables/activity";
import {
	cleanThought,
	fmtCost,
	fmtElapsed,
	fmtTokens,
	summarizeActions,
	type TurnMeter,
	type TurnTokens,
} from "../composables/activity";
import type { LiveTool } from "../composables/bubble";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import ToolMediaPreview from "./ToolMediaPreview.vue";

/**
 * O QUE O AGENTE FEZ PARA CHEGAR ÀQUILO — raciocínio, ferramentas e conta, num
 * cartão só.
 *
 * A peça que a V2 tinha e a V3 não (`AgentActivityConsolidated.vue`). O painel da
 * V3 espalhava a mesma informação por três sítios com regras próprias: uma gaveta
 * `<details>` para o raciocínio, uma lista `ToolSteps` para os passos e um número
 * solto de tokens na barra de baixo. Três coisas a acontecer, quando é UMA — e o
 * olho tinha de as remontar.
 *
 * ── O QUE ESTE COMPONENTE NÃO SABE ────────────────────────────────────────
 *
 * Quem está a olhar. Nada aqui pergunta "sou admin?": todo o desenho passa por
 * `show.<eixo>`, e os eixos vêm de duas linhas de `.env` no servidor
 * (`AGENT_DISPLAY_CLIENT` / `AGENT_DISPLAY_DEV`). É o princípio que a V2 escreveu
 * em cima do seu próprio resolvedor e que se ganha aqui de graça: mudar o que um
 * dev vê é editar configuração, não caçar `v-if` por seis ficheiros.
 *
 * ── NADA ABRE SOZINHO ─────────────────────────────────────────────────────
 *
 * O cartão nasce FECHADO, em toda a superfície e em todo o modo — a decisão do
 * dono a 24/08. O eixo `expanded`, que o abria por política, foi retirado do
 * contrato dos dois lados do fio: era o que punha uma caixa "Raciocínio" aberta e
 * muitas vezes vazia por baixo de cada resposta.
 *
 * ── VÊ, MAS NÃO ABRE ──────────────────────────────────────────────────────
 *
 * A decisão do dono, 24/08: toda a gente lê a lista inteira do que o agente fez e
 * quanto cada coisa demorou; só o modo DEV é que CLICA num passo e vê a entrada e
 * a saída cruas. É um eixo só — `toolIo` — e é ele que decide se a linha é um
 * `<button>` ou um `<div>`. Uma linha que parece clicável e não faz nada é pior
 * do que uma linha que não parece.
 */
const props = defineProps<{
	tools: readonly LiveTool[];
	/** O que o modelo escreveu antes da resposta. */
	thinking?: string;
	/** O turno está a acontecer: a pílula pulsa e o título muda. Abrir continua a
	 *  ser gesto de pessoa — ver `open`. */
	live?: boolean;
	/** Quem respondeu e quanto custou. Só o rodapé DEV o lê. */
	meter?: TurnMeter | null;
	tokens?: TurnTokens | null;
	/** Só uma metade: os passos vão ACIMA da resposta e a conta abaixo, como no Claude. */
	part?: "steps" | "meter";
}>();

const { t, toolLabel, locale, count } = useAgentsI18n();
const { show, audience } = useAgentDisplayMode();
const execution = computed(() =>
	audience.value === "dev" && show.value.model
		? executionFacts(props.meter?.execution, locale.value)
		: [],
);

/** Que passos estão abertos. Só existe quando `toolIo` está ligado. */
const opened = ref<ReadonlySet<string>>(new Set());

/**
 * FECHADO, SEMPRE — até alguém clicar.
 *
 * A decisão do dono, 24/08: nenhum painel recolhível abre sozinho ao montar.
 * Havia duas políticas a abri-lo — o eixo `expanded` (ligado no modo dev) e o
 * turno ao vivo — e o efeito era uma caixa "Raciocínio" já aberta debaixo de cada
 * resposta, muitas vezes com nada dentro além da linha de tokens, colada na barra
 * onde vive o "regerar". O eixo foi retirado da política inteira, dos dois lados
 * do fio; o que resta é a pílula, que diz o que houve e quanto demorou, e abre
 * quando se pede.
 */
const open = ref(false);

/**
 * AO VIVO, ABERTO — a exceção que a regra de 24/08 não previa.
 *
 * "Nenhum painel recolhível abre sozinho" é sobre a fala GRAVADA: uma caixa já
 * aberta debaixo de cada resposta antiga é ruído. O turno EM CURSO é outra
 * coisa — é o único sinal de vida enquanto um modelo pensa por vinte segundos,
 * e com ele fechado a tela fica morta até a resposta cair inteira ("o stream
 * sumiu", dono, 26/08 — o raciocínio estava streamando por baixo da pílula
 * fechada o tempo todo). Ao vivo não é gaveta, é progresso: abre enquanto
 * `live`, e a fala que o turno vira volta a obedecer a regra — fechada, com o
 * clique (ou o pino do DEV) mandando.
 */
const expanded = computed(() => props.live || open.value);

const hasTools = computed(() => show.value.tools && props.tools.length > 0);
const hasThinking = computed(() => show.value.reasoning && !!props.thinking?.trim());
/** O rodapé só existe se algum dos seus quatro números for para mostrar E existir. */
const hasFooter = computed(() => {
	const s = show.value;
	if (s.model && props.meter?.model) return true;
	if (execution.value.length) return true;
	if (s.usage && props.tokens) return true;
	if (s.cost && props.meter?.costMicroUsd !== undefined) return true;
	if (s.credits && props.meter?.credits != null) return true;
	return false;
});

const visible = computed(
	() =>
		show.value.activity &&
		(props.part === "meter"
			? hasFooter.value
			: props.part === "steps"
				? hasTools.value || hasThinking.value || props.live
				: hasTools.value || hasThinking.value || hasFooter.value),
);

/** Há um corpo para abrir. Sem isto, uma fala que só tem a conta desenharia uma
 *  pílula expansível sobre o vazio — a linha já está à vista, fora da gaveta. */
const hasBody = computed(() => hasTools.value || hasThinking.value);

/**
 * O total do turno: o que o servidor mediu, ou a soma dos passos.
 *
 * O servidor é a fonte melhor — ele conta também o tempo em que o modelo estava a
 * pensar entre duas ferramentas, que é onde um turno lento costuma estar. A soma
 * dos passos é o que resta quando não há `meter` (ao vivo, e nas falas gravadas
 * antes de o `meter` existir).
 */
const totalMs = computed(() => {
	if (props.meter?.durationMs !== undefined) return props.meter.durationMs;
	return props.tools.reduce((sum, tool) => sum + (tool.ms ?? 0), 0);
});

const title = computed(() => {
	if (props.live) return t("live.thinking");
	return hasTools.value ? t("live.activity") : t("live.reasoning");
});

/**
 * O parágrafo do raciocínio, com a precedência da V2.
 *
 *   `reasoningRaw` ligado  → o trace do modelo, com formatação Markdown segura.
 *   a correr               → o raciocínio ao vivo, sem corte: é o que está a ser
 *                            escrito, e cortá-lo é esconder o fim da frase.
 *   parado, sem `raw`      → a LISTA DE AÇÕES, nunca o texto cru do modelo.
 *
 * A última linha é a que interessa: o pensamento cru de um modelo debaixo de uma
 * resposta lê-se como parte dela, e não é.
 */
const summary = computed(() => {
	if (props.live) return props.thinking ?? "";
	const actions = summarizeActions(props.tools.map((tool) => toolLabel(tool.name, tool.input)));
	return actions || cleanThought(props.thinking ?? "");
});

function toggle(): void {
	open.value = !open.value;
}

/** Quantas tarefas de um passo ficam à vista — as últimas; a contagem diz o resto. */
const TASKS_SHOWN = 8;
const TASK_ICON: Record<ToolTask["status"], IconName> = {
	queued: "clock",
	running: "hourglass",
	succeeded: "check",
	failed: "alert",
	cancelled: "close",
};

function taskClass(status: ToolTask["status"]): string {
	if (status === "succeeded") return "text-success";
	if (status === "failed") return "text-danger";
	if (status === "cancelled") return "text-fg-subtle";
	return "animate-pulse text-fg-subtle";
}

function runningOf(tasks: readonly ToolTask[]): number {
	return tasks.filter((task) => task.status === "running" || task.status === "queued").length;
}

function toggleStep(id: string): void {
	const next = new Set(opened.value);
	if (next.has(id)) next.delete(id);
	else next.add(id);
	opened.value = next;
}
</script>

<template>
	<div v-if="visible" class="text-xs">
		<!-- O CABEÇALHO É UMA PÍLULA, e não uma linha de texto: ele é um controlo, e
		     tem de o parecer antes de alguém tentar clicar. -->
		<button
			v-if="part !== 'meter' && (hasBody || live)"
			type="button"
			class="flex w-full min-w-0 items-center gap-2 py-1 text-left text-sm text-fg-subtle transition-colors hover:text-fg"
			:aria-expanded="expanded"
			:aria-label="expanded ? t('live.collapse') : t('live.expand')"
			@click="toggle"
		>
			<span v-if="live" class="animate-pulse" aria-hidden="true">•••</span>
			<Icon v-else name="clock" class="size-4" />
			<span class="shrink-0">{{ title }}</span>
			<span v-if="hasTools" class="shrink-0">
				· {{ tools.length }} {{ tools.length === 1 ? t("steps.one") : t("steps.many") }}
			</span>
			<span v-if="show.duration && totalMs > 0" class="shrink-0 tabular-nums">
				· {{ fmtElapsed(totalMs) }}
			</span>
			<span
				class="ml-auto shrink-0 text-base leading-none transition-transform"
				:class="expanded ? 'rotate-90' : ''"
				aria-hidden="true"
				>›</span
			>
		</button>

		<div v-if="part !== 'meter' && expanded" class="mt-1 space-y-2 border-l border-border pl-2.5">
			<!-- ── A LINHA DO TEMPO DAS FERRAMENTAS ─────────────────────────── -->
			<ul v-if="hasTools" class="space-y-0.5">
				<li v-for="tool in tools" :key="tool.id">
					<component
						:is="show.toolIo && tool.output !== undefined ? 'button' : 'div'"
						:type="show.toolIo && tool.output !== undefined ? 'button' : undefined"
						class="flex w-full min-w-0 items-start gap-1.5 py-0.5 text-left"
						:class="show.toolIo && tool.output !== undefined ? 'cursor-pointer' : ''"
						:aria-expanded="show.toolIo && tool.output !== undefined ? opened.has(tool.id) : undefined"
						@click="show.toolIo && tool.output !== undefined ? toggleStep(tool.id) : undefined"
					>
						<!-- O SELO. Um passo à espera pulsa: "está a trabalhar" e "parou"
						     não podem parecer a mesma coisa. -->
						<span
							v-if="tool.output === undefined"
							class="mt-px shrink-0 animate-pulse text-fg-subtle"
							:aria-label="t('live.running')"
							>◌</span
						>
						<Icon
							v-else
							:name="tool.isError ? 'close' : 'check'"
							class="mt-0.5 size-3.5"
							:class="tool.isError ? 'text-danger' : 'text-success'"
						/>

						<span class="min-w-0 flex-1">
							<!-- O CÓDIGO CRU só no modo que sabe o que fazer com ele. Para
							     todos os outros, a frase — `write_structure` não se audita. -->
							<code v-if="show.toolRaw" class="font-mono text-2xs text-fg">{{ tool.name }}</code>
							<span v-else class="text-fg" :title="tool.name">{{ toolLabel(tool.name, tool.input) }}</span>
							<span v-if="isPending(tool)" class="text-fg-subtle">…</span>
							<span v-if="show.toolIo && tool.input" class="block truncate text-2xs text-fg-muted">
								{{ tool.input }}
							</span>
						</span>

						<span
							v-if="show.toolDuration && tool.ms !== undefined"
							class="shrink-0 tabular-nums text-2xs text-fg-subtle"
							>{{ tool.ms }}ms</span
						>
					</component>

					<!-- AS TAREFAS DO PASSO — o filho do `spawn_agent` trabalhando, ferramenta a
					     ferramenta. Só as últimas: um filho de 30 passos empurraria a resposta
					     para fora da tela; a contagem diz o resto. -->
					<ul v-if="tool.tasks?.length" class="mb-1 ml-1.5 space-y-0.5 border-l border-border pl-2 text-2xs">
						<li v-if="tool.tasks.length > TASKS_SHOWN" class="text-fg-subtle">
							{{ t("live.tasks", { n: tool.tasks.length, running: runningOf(tool.tasks) }) }}
						</li>
						<li
							v-for="task in tool.tasks.slice(-TASKS_SHOWN)"
							:key="task.id"
							class="flex min-w-0 items-center gap-1.5"
						>
							<Icon :name="TASK_ICON[task.status]" class="size-3" :class="taskClass(task.status)" />
							<span class="min-w-0 truncate text-fg-muted" :title="task.label">{{ task.label }}</span>
						</li>
					</ul>

					<!-- A ENTRADA e o RETORNO crus. Existem só sob `toolIo` — também o
					     passo aberto antes de trocar para a visão de cliente (27/09). -->
					<div v-if="show.toolIo && opened.has(tool.id)" class="flex flex-col gap-1 pb-1.5">
						<div v-for="part in [
							{ key: 'input', text: tool.input },
							{ key: 'output', text: tool.output },
						]" :key="part.key" class="min-w-0">
							<span class="mb-0.5 block text-3xs uppercase tracking-wide text-fg-subtle">
								{{ t(`steps.${part.key}`) }}
							</span>
							<ToolMediaPreview
								v-if="show.toolMedia && part.key === 'output'"
								:text="part.text ?? ''"
							/>
							<!-- JSON longo rola na PRÓPRIA caixa: a página nunca rola de lado. -->
							<pre
								class="max-h-56 overflow-auto rounded-sm bg-bg-muted px-2 py-1.5 font-mono text-2xs leading-relaxed break-words whitespace-pre-wrap"
								:class="part.key === 'output' && tool.isError ? 'text-danger' : ''"
								>{{ part.text }}</pre
							>
						</div>
					</div>
				</li>
			</ul>

			<!-- ── O RACIOCÍNIO ─────────────────────────────────────────────── -->
			<template v-if="hasThinking">
				<div v-if="show.reasoningRaw">
					<span class="mb-0.5 block text-3xs uppercase tracking-wide text-fg-subtle">
						{{ t("live.reasoningRaw") }}
					</span>
					<RichText
						class="max-h-64 overflow-auto rounded-sm bg-bg-muted px-2 py-1.5 text-2xs leading-relaxed break-words"
						:text="thinking ?? ''" origin="contact"
					/>
				</div>
				<RichText
					v-else-if="summary"
					class="max-h-36 overflow-auto text-2xs leading-relaxed text-fg-muted"
					:text="summary" origin="contact"
				/>
			</template>

		</div>

		<!-- ── A LINHA DA CONTA: modelo · tokens · créditos · custo ──────────
		     FORA da gaveta, de propósito (dono, 01/09, com o print da V2 na mão):
		     a conta do turno fica À VISTA sob a resposta, sem pedir clique — é
		     uma linha de facto, não um painel que abre. Quem decide se ela existe
		     continua a ser a política de eixos, nunca este ficheiro. -->
		<div
			v-if="part !== 'steps' && hasFooter"
			class="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-dashed border-border pt-1 font-mono text-2xs text-fg-subtle"
		>
			<span v-if="show.model && meter?.model" class="inline-flex items-center gap-1" :title="meter.provider ?? ''">
				<Icon name="bot" class="size-3" />{{ meter.model }}
			</span>
			<span v-for="fact in execution" :key="fact">{{ fact }}</span>
			<span v-if="show.usage && tokens" class="tabular-nums">
				↧{{ fmtTokens(tokens.input) }} ↥{{ fmtTokens(tokens.output) }}
				<template v-if="tokens.cached"> ⧉{{ fmtTokens(tokens.cached) }}</template>
				<template v-if="tokens.reasoning"> ✻{{ fmtTokens(tokens.reasoning) }}</template>
			</span>
			<span v-if="show.credits && meter?.credits != null" class="tabular-nums">
				{{ t("live.credits", { n: count(meter.credits) }) }}
			</span>
			<span
				v-if="show.cost && meter?.costMicroUsd !== undefined"
				class="tabular-nums text-success"
			>
				{{ fmtCost(meter.costMicroUsd, locale) }}
			</span>
		</div>
	</div>
</template>
